/* Nuelune theme JS: reveal, gallery, buy box, sticky bar, cart drawer, parallax. No dependencies. */
(function () {
  'use strict';
  const N = window.Nuelune || {};
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Money ---------- */
  function formatMoney(cents) {
    const fmt = N.moneyFormat || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    return fmt.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, value.replace(/\B(?=(\d{3})+(?!\d))/g, ','));
  }

  /* ---------- Reveal on scroll ---------- */
  function initReveal(root) {
    const els = (root || document).querySelectorAll('.reveal:not(.is-visible)');
    if (reduceMotion || !('IntersectionObserver' in window)) { els.forEach((el) => el.classList.add('is-visible')); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); } });
    }, { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
  }

  /* ---------- Gentle parallax (desktop + mouse only) ---------- */
  function initParallax() {
    if (reduceMotion || !window.matchMedia('(min-width: 900px) and (hover: hover)').matches) return;
    const layers = document.querySelectorAll('[data-parallax]');
    if (!layers.length) return;
    let ticking = false;
    function update() {
      layers.forEach((el) => {
        const rect = el.parentElement.getBoundingClientRect();
        const offset = Math.max(-24, Math.min(24, (rect.top + rect.height / 2 - window.innerHeight / 2) * -0.04));
        el.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0)';
      });
      ticking = false;
    }
    window.addEventListener('scroll', () => { if (!ticking) { requestAnimationFrame(update); ticking = true; } }, { passive: true });
    update();
  }

  /* ---------- Gallery (swipe / scroll-snap, thumbs, arrows, dots) ---------- */
  function initGallery(root) {
    (root || document).querySelectorAll('[data-gallery]').forEach((g) => {
      const track = g.querySelector('[data-gallery-track]');
      if (!track) return;
      const slides = () => track.querySelectorAll('[data-gallery-slide]');
      const thumbs = g.querySelectorAll('[data-gallery-thumb]');
      const dots = g.querySelectorAll('[data-gallery-dot]');
      const current = () => Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      const goTo = (i, smooth) => {
        const n = slides().length;
        const idx = Math.max(0, Math.min(n - 1, i));
        track.scrollTo({ left: idx * track.clientWidth, behavior: smooth === false ? 'instant' : 'smooth' });
      };
      const mark = () => {
        const i = current();
        thumbs.forEach((t, k) => t.setAttribute('aria-current', k === i ? 'true' : 'false'));
        dots.forEach((d, k) => d.classList.toggle('is-active', k === i));
      };
      let raf = null;
      track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(mark); }, { passive: true });
      thumbs.forEach((t) => t.addEventListener('click', () => goTo(parseInt(t.dataset.index || '0', 10))));
      const prev = g.querySelector('[data-gallery-prev]');
      const next = g.querySelector('[data-gallery-next]');
      if (prev) prev.addEventListener('click', () => goTo(current() - 1));
      if (next) next.addEventListener('click', () => goTo(current() + 1));
      track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(current() - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); goTo(current() + 1); }
      });
      g.galleryGoTo = goTo;
    });
  }

  /* ---------- Buy box (bundle cards + variant select) ---------- */
  function initBuyBox(root) {
    (root || document).querySelectorAll('[data-buy-box]').forEach((box) => {
      const idInput = box.querySelector('input[name="id"]');
      const atc = box.querySelector('[data-atc]');
      const atcLabel = box.querySelector('[data-atc-label]');
      const sticky = document.querySelector('[data-sticky-for="' + box.id + '"]');

      function setSelection(variantId, priceCents, available, label) {
        if (idInput) idInput.value = variantId || '';
        const priceText = priceCents ? ' — ' + formatMoney(priceCents) : '';
        if (atc) atc.disabled = !variantId || !available;
        if (atcLabel) atcLabel.textContent = (available === false ? N.strings.soldOut : N.strings.addToCart) + priceText;
        if (sticky) {
          const sl = sticky.querySelector('[data-sticky-label]');
          const sb = sticky.querySelector('[data-sticky-btn]');
          if (sl && label) sl.textContent = label;
          if (sb) { sb.textContent = N.strings.addToCart + priceText; sb.disabled = !variantId || !available; }
        }
      }

      const section = box.closest('.product-section');
      const gallery = section && section.querySelector('[data-gallery]');
      function showOptionImage(opt) {
        const tpl = opt.closest('.bundle') && opt.closest('.bundle').querySelector('template[data-bundle-image]');
        const stickyImg = sticky && sticky.querySelector('[data-sticky-img]');
        const src = tpl && tpl.content.querySelector('img');
        if (stickyImg && src) stickyImg.innerHTML = '<img src="' + src.getAttribute('src') + '" alt="" width="56" height="56">';
        const firstSlide = gallery && gallery.querySelector('[data-gallery-slide]');
        if (!tpl || !firstSlide) return;
        firstSlide.innerHTML = tpl.innerHTML;
        if (gallery.galleryGoTo) gallery.galleryGoTo(0, false);
      }
      box.querySelectorAll('[data-bundle-option]').forEach((opt) => {
        opt.addEventListener('change', () => {
          setSelection(opt.dataset.variantId, parseInt(opt.dataset.price || '0', 10), opt.dataset.available !== 'false', opt.dataset.label);
          showOptionImage(opt);
        });
        if (opt.checked) showOptionImage(opt);
      });
      const select = box.querySelector('[data-variant-select]');
      if (select) select.addEventListener('change', () => {
        const o = select.selectedOptions[0];
        setSelection(o.value, parseInt(o.dataset.price || '0', 10), o.dataset.available !== 'false');
      });

      const form = box.querySelector('form[data-product-form]');
      if (form) form.addEventListener('submit', (e) => {
        e.preventDefault();
        if (!idInput || !idInput.value) return;
        addToCart(idInput.value, 1, atc);
      });
      if (sticky) {
        const sb = sticky.querySelector('[data-sticky-btn]');
        if (sb) sb.addEventListener('click', () => { if (idInput && idInput.value) addToCart(idInput.value, 1, sb); });
        if (atc && 'IntersectionObserver' in window) {
          new IntersectionObserver(([e]) => {
            // show whenever the main button is off screen, also before it (on mobile it sits below the fold)
            sticky.classList.toggle('is-visible', !e.isIntersecting);
          }).observe(atc);
        }
      }
    });
  }

  /* ---------- Cart ---------- */
  const drawer = document.getElementById('CartDrawer');
  let lastFocus = null;

  function addToCart(variantId, qty, btn) {
    if (btn) btn.setAttribute('aria-busy', 'true');
    return fetch((N.routes.cart_add_url || '/cart/add') + '.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: qty || 1 }] })
    }).then((r) => r.json()).then(() => refreshCart(true))
      .catch(() => { window.location.href = N.routes.cart_url || '/cart'; })
      .finally(() => { if (btn) btn.removeAttribute('aria-busy'); });
  }

  function changeLine(key, qty) {
    return fetch((N.routes.cart_change_url || '/cart/change') + '.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    }).then((r) => r.json()).then(renderCart);
  }

  function refreshCart(open) {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()).then((cart) => {
      renderCart(cart);
      if (open) openDrawer();
    });
  }

  function renderCart(cart) {
    const protect = drawer && drawer.querySelector('[data-cart-protect]');
    const protectId = protect ? protect.dataset.variantId : null;
    const protectLine = protectId ? cart.items.find((i) => String(i.variant_id) === protectId) : null;
    const visibleCount = cart.item_count - (protectLine ? protectLine.quantity : 0);
    document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = visibleCount; el.hidden = visibleCount === 0; });
    if (!drawer) return;
    // protection only makes sense with products in the cart; remove it when the cart is otherwise empty
    if (protectLine && visibleCount === 0) { changeLine(protectLine.key, 0); return; }
    if (protect) {
      protect.hidden = visibleCount === 0;
      const box = protect.querySelector('[data-protect-toggle]');
      box.checked = !!protectLine;
      protect.protectLine = protectLine || null;
    }
    const list = drawer.querySelector('[data-cart-items]');
    const empty = drawer.querySelector('[data-cart-empty]');
    const subtotal = drawer.querySelector('[data-cart-subtotal]');
    list.innerHTML = '';
    cart.items.forEach((item) => {
      if (protectId && String(item.variant_id) === protectId) return;   // shown as the toggle instead
      const li = document.createElement('li');
      li.className = 'cart-item';
      const img = item.image ? '<img src="' + item.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2') + '" alt="" width="72" height="72" loading="lazy">' : '<span class="cart-item__ph"></span>';
      li.innerHTML =
        '<div class="cart-item__media">' + img + '</div>' +
        '<div class="cart-item__info"><a href="' + item.url + '" class="cart-item__title">' + escapeHtml(item.product_title) + '</a>' +
        (item.variant_title && item.variant_title !== 'Default Title' ? '<span class="muted cart-item__variant">' + escapeHtml(item.variant_title) + '</span>' : '') +
        '<div class="qty"><button type="button" data-qty="-1" aria-label="Decrease quantity">−</button><span>' + item.quantity + '</span><button type="button" data-qty="1" aria-label="Increase quantity">+</button></div></div>' +
        '<div class="cart-item__end"><b>' + formatMoney(item.final_line_price) + '</b><button type="button" class="link-button" data-remove>' + N.strings.remove + '</button></div>';
      li.querySelectorAll('[data-qty]').forEach((b) => b.addEventListener('click', () => changeLine(item.key, Math.max(0, item.quantity + parseInt(b.dataset.qty, 10)))));
      li.querySelector('[data-remove]').addEventListener('click', () => changeLine(item.key, 0));
      list.appendChild(li);
    });
    empty.hidden = visibleCount > 0;
    subtotal.textContent = formatMoney(cart.total_price);

    const threshold = N.freeShippingThreshold || 0;
    const text = drawer.querySelector('[data-cart-progress-text]');
    const bar = drawer.querySelector('[data-cart-progress-bar]');
    if (threshold > 0) {
      const pct = Math.min(100, Math.round((cart.total_price / threshold) * 100));
      bar.firstElementChild.style.width = pct + '%';
      bar.setAttribute('aria-valuenow', pct);
      text.textContent = cart.total_price >= threshold ? N.strings.freeShippingReached : N.strings.freeShippingAway.replace('[amount]', formatMoney(threshold - cart.total_price));
    } else { drawer.querySelector('[data-cart-progress]').hidden = true; }
    const shipNote = drawer.querySelector('[data-cart-shipping-note]');
    if (shipNote) {
      const below = threshold > 0 && cart.item_count > 0 && cart.total_price < threshold;
      shipNote.hidden = !below;
      if (below && N.strings.shippingNote) shipNote.textContent = N.strings.shippingNote.replace('[amount]', formatMoney(threshold));
    }

    let upgradeShown = false;
    drawer.querySelectorAll('[data-cart-upgrade]').forEach((upgrade) => {
      upgrade.hidden = true;
      if (upgradeShown) return;                       // one offer at a time
      const fromIds = (upgrade.dataset.from || '').split(',').filter(Boolean);
      if (cart.items.some((i) => String(i.variant_id) === upgrade.dataset.variantId)) return;
      const seen = {};
      const lines = cart.items.filter((i) => {
        const pid = String(i.product_id);
        if (!fromIds.includes(pid) || seen[pid]) return false;
        seen[pid] = true; return true;
      });
      const mode = upgrade.dataset.mode;
      const has = (pid) => lines.some((l) => String(l.product_id) === pid);
      const eligible = mode === 'all' ? lines.length === fromIds.length
        : mode === 'first' ? has(fromIds[0]) && fromIds.slice(1).some((id) => !has(id))   // first required; only offered while the rest is NOT yet in the cart (adding it via the upgrade)
        : lines.length > 0;
      if (!eligible) return;
      const info = {};
      (upgrade.dataset.fromInfo || '').split(',').forEach((x) => { const [id, price, title] = x.split(':'); if (id) info[id] = { price: parseInt(price || '0', 10), title: title || '' }; });
      const setPrice = parseInt(upgrade.dataset.price || '0', 10);
      const current = lines.reduce((sum, l) => sum + l.final_price, 0);
      const diff = setPrice - current;
      const missing = fromIds.filter((id) => !has(id));
      const missingValue = missing.reduce((sum, id) => sum + ((info[id] && info[id].price) || 0), 0);
      const parts = [];
      if (diff > 0) {
        parts.push('+' + formatMoney(diff));
        const names = missing.map((id) => (info[id] && info[id].title) || '').filter(Boolean);
        if (names.length) parts.push('adds ' + names.join(' & '));
        if (missingValue > diff) parts.push('save ' + formatMoney(missingValue - diff));
      } else {
        parts.push('Save ' + formatMoney(-diff));
      }
      if (diff > 0 && N.freeShippingThreshold && cart.total_price < N.freeShippingThreshold && cart.total_price + diff >= N.freeShippingThreshold) parts.push('Free US shipping');
      upgrade.querySelector('[data-upgrade-text]').textContent = parts.join(' · ');
      upgrade.upgradeLines = lines.map((l) => ({ key: l.key, quantity: l.quantity }));
      upgrade.hidden = false;
      upgradeShown = true;
    });

    // products already included in a set that is in the cart (e.g. the Remover inside the Full Routine)
    const covered = new Set();
    drawer.querySelectorAll('[data-cart-upgrade]').forEach((u) => {
      if (cart.items.some((i) => String(i.variant_id) === u.dataset.variantId)) {
        (u.dataset.from || '').split(',').filter(Boolean).forEach((id) => covered.add(id));
      }
    });
    const upsell = drawer.querySelector('[data-cart-upsell]');
    if (upsell) {
      let visible = 0;
      upsell.querySelectorAll('[data-upsell-item]').forEach((row) => {
        const isCovered = covered.has(row.dataset.productId) || (row.dataset.coveredBy && cart.items.some((i) => String(i.variant_id) === row.dataset.coveredBy));
        const inCart = isCovered || cart.items.some((i) => String(i.variant_id) === row.dataset.variantId || String(i.product_id) === row.dataset.productId);
        row.hidden = inCart;
        if (!inCart) visible += 1;
      });
      upsell.hidden = visible === 0 || cart.item_count === 0 || upgradeShown;
    }
  }

  async function upgradeToSet(btn) {
    const upgrade = btn.closest('[data-cart-upgrade]');
    if (!upgrade || !upgrade.upgradeLines) return;
    btn.setAttribute('aria-busy', 'true');
    try {
      for (const l of upgrade.upgradeLines) {
        await fetch((N.routes.cart_change_url || '/cart/change') + '.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: l.key, quantity: Math.max(0, l.quantity - 1) })
        });
      }
      await addToCart(upgrade.dataset.variantId, 1);
    } finally { btn.removeAttribute('aria-busy'); }
  }

  function escapeHtml(s) { return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }

  function openDrawer() {
    if (!drawer) return;
    lastFocus = document.activeElement;
    drawer.setAttribute('aria-hidden', 'false');
    drawer.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
    drawer.querySelector('.cart-drawer__panel').focus();
  }
  function closeDrawer() {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    drawer.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    if (lastFocus) lastFocus.focus();
  }

  function initCart() {
    if (!drawer) return;
    drawer.querySelectorAll('[data-cart-close]').forEach((b) => b.addEventListener('click', closeDrawer));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer(); });
    document.querySelectorAll('[data-cart-open]').forEach((a) => a.addEventListener('click', (e) => { e.preventDefault(); refreshCart(true); }));
    drawer.querySelectorAll('[data-upgrade-btn]').forEach((b) => b.addEventListener('click', () => upgradeToSet(b)));
    const protectBox = drawer.querySelector('[data-protect-toggle]');
    if (protectBox) protectBox.addEventListener('change', () => {
      const wrap = protectBox.closest('[data-cart-protect]');
      protectBox.disabled = true;
      const done = () => { protectBox.disabled = false; };
      if (protectBox.checked) addToCart(wrap.dataset.variantId, 1).finally(done);
      else if (wrap.protectLine) changeLine(wrap.protectLine.key, 0).finally(done);
      else done();
    });
    drawer.querySelectorAll('[data-upsell-add]').forEach((b) => b.addEventListener('click', () => addToCart(b.closest('[data-upsell-item]').dataset.variantId, 1, b)));
    refreshCart(false);
  }


  /* ---------- Newsletter pop-up ---------- */
  function initPopup() {
    const pop = document.querySelector('[data-newsletter-popup]');
    if (!pop) return;
    const KEY = 'nuelune_popup';
    const store = {
      get() { try { return JSON.parse(localStorage.getItem(KEY) || '{}'); } catch (e) { return {}; } },
      set(v) { try { localStorage.setItem(KEY, JSON.stringify(v)); } catch (e) { /* storage blocked: fine */ } }
    };
    const dialog = pop.querySelector('.nl-popup__dialog');
    const fromPopup = /NewsletterPopup/.test(window.location.hash);
    const success = pop.querySelector('[data-popup-success]');
    const error = pop.querySelector('[data-popup-error]');
    let lastFocus = null;

    function open() {
      if (!pop.hidden) return;
      lastFocus = document.activeElement;
      pop.hidden = false;
      requestAnimationFrame(() => pop.classList.add('is-open'));
      document.body.classList.add('overflow-hidden');
      const first = pop.querySelector('input[type="email"]') || dialog;
      setTimeout(() => first.focus(), 50);
    }
    function close() {
      pop.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      setTimeout(() => { pop.hidden = true; }, 250);
      const st = store.get();
      if (!st.subscribed) store.set(Object.assign(st, { snoozedAt: Date.now() }));
      if (lastFocus) lastFocus.focus();
    }
    pop.querySelectorAll('[data-popup-close]').forEach((b) => b.addEventListener('click', close));
    const copyBtn = pop.querySelector('[data-popup-copy]');
    if (copyBtn) copyBtn.addEventListener('click', () => {
      const code = (pop.querySelector('[data-popup-code]') || {}).textContent || '';
      const done = () => { copyBtn.textContent = copyBtn.dataset.copied || 'Copied'; };
      if (navigator.clipboard) navigator.clipboard.writeText(code.trim()).then(done).catch(done); else done();
    });
    document.addEventListener('keydown', (e) => {
      if (pop.hidden) return;
      if (e.key === 'Escape') close();
      if (e.key === 'Tab') {                       // keep focus inside the dialog
        const els = dialog.querySelectorAll('button, input, a[href]');
        if (!els.length) return;
        const firstEl = els[0], lastEl = els[els.length - 1];
        if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
        else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
      }
    });
    const form = pop.querySelector('form');
    if (form) form.addEventListener('submit', () => store.set(Object.assign(store.get(), { subscribed: true })));

    // after a sign-up anywhere on the site: never show again; only re-open to show the result of a pop-up sign-up
    if (success) {
      store.set(Object.assign(store.get(), { subscribed: true }));
      if (fromPopup) open();
      return;
    }
    if (error && fromPopup) { open(); return; }

    const st = store.get();
    if (st.subscribed) return;
    const snoozeMs = (parseInt(pop.dataset.snoozeDays || '14', 10)) * 864e5;
    if (st.snoozedAt && Date.now() - st.snoozedAt < snoozeMs) return;

    const delay = Math.max(3, parseInt(pop.dataset.delay || '10', 10)) * 1000;
    // only after the delay (no scroll trigger: on product pages that fired right at the add-to-cart button)
    // show at most once: the first trigger (timer or exit intent) counts as the one showing, then snooze
    let shown = false;
    function openOnce() {
      if (shown) return;
      shown = true;
      clearTimeout(timer);
      document.removeEventListener('mouseout', onExit);
      store.set(Object.assign(store.get(), { snoozedAt: Date.now() }));
      open();
    }
    function onExit(e) { if (!e.relatedTarget && e.clientY <= 0) openOnce(); }
    const timer = setTimeout(openOnce, delay);
    if (window.matchMedia('(hover: hover)').matches) document.addEventListener('mouseout', onExit);
  }

  /* ---------- Mobile menu ---------- */
  function initMenu() {
    const toggle = document.querySelector('[data-menu-toggle]');
    const menu = document.getElementById('MobileMenu');
    if (!toggle || !menu) return;
    toggle.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!open));
      menu.hidden = open;
    });
  }

  function init(root) { initReveal(root); initGallery(root); initBuyBox(root); }

  document.addEventListener('DOMContentLoaded', () => { init(document); initCart(); initMenu(); initParallax(); initPopup(); });
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
